import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const DISMISSED_KEY = 'versionUpdateDismissed';

interface VersionInfo {
  version: string;
  buildTime: string;
}

// Version baked in at build time via Dockerfile ARG → ENV → REACT_APP_*
const CURRENT_VERSION =
  process.env.REACT_APP_GIT_COMMIT_HASH || 'dev';

async function fetchDeployedVersion(): Promise<VersionInfo | null> {
  try {
    // Cache-busting query param so the browser never returns a stale version.json
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return (await res.json()) as VersionInfo;
  } catch {
    return null;
  }
}

function isNewVersionAvailable(deployed: VersionInfo): boolean {
  // Skip check entirely in local dev (version is always "dev")
  if (CURRENT_VERSION === 'dev' || deployed.version === 'dev') return false;
  return deployed.version !== CURRENT_VERSION;
}

/**
 * Polls /version.json every POLL_INTERVAL_MS.
 * Returns `updateAvailable: true` when the deployed version differs from
 * the version baked into this build.
 * Also returns `dismiss()` so callers can suppress further notifications.
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const dismiss = () => {
    setUpdateAvailable(false);
    sessionStorage.setItem(DISMISSED_KEY, CURRENT_VERSION);
  };

  useEffect(() => {
    const check = async () => {
      const deployed = await fetchDeployedVersion();
      if (!deployed) return;

      // Don't re-show if the user already dismissed this specific version
      const dismissed = sessionStorage.getItem(DISMISSED_KEY);
      if (dismissed === deployed.version) return;

      if (isNewVersionAvailable(deployed)) {
        setUpdateAvailable(true);
      }
    };

    // First check shortly after mount (10s) so we don't immediately interrupt login
    const initialTimer = setTimeout(check, 10_000);

    // Then poll on a regular interval
    timerRef.current = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return { updateAvailable, dismiss };
}
